import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';
import logger from '../lib/logger';

const AuthContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// Select every column the UI actually reads across ALL components:
//   ProfileDropdown  → display_name, avatar_url, username, bio, location,
//                      favorite_genres, role
//   FanProfile       → display_name, avatar_url, username, bio, location,
//                      website, instagram, twitter, favorite_genres, role
//   AuthContext      → id, role, access_status, onboarded, label_id
//   OnboardingGate   → onboarded
// ─────────────────────────────────────────────────────────────────────────────
const PROFILE_SELECT = [
  'id',
  'role',
  'display_name',
  'username',
  'avatar_url',
  'bio',
  'location',
  'website',
  'instagram',
  'twitter',
  'favorite_genres',
  'access_status',
  'onboarded',
  'label_id',
  'created_at',
].join(',');

export const AuthProvider = ({ children }) => {
  const [session,        setSession]        = useState(undefined);
  const [user,           setUser]           = useState(null);
  const [profile,        setProfile]        = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError,   setProfileError]   = useState(null);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setProfileLoading(false);
      setProfileError(null);
      return;
    }

    setProfileLoading(true);
    setProfileError(null);

    try {
      const { data, error } = await supabase
        .from('d2f_profiles')
        .select(PROFILE_SELECT)
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        // Log the raw error in dev so we can diagnose RLS/column issues
        logger.error('[AuthContext] fetchProfile DB error', {
          code:    error.code,
          hint:    error.hint,
          details: error.details,
          // message may contain table/column names — safe to log (no PII)
          message: error.message,
        });
        throw error;
      }

      if (!data) {
        // Profile row missing — try JWT role fallback for admin accounts
        // whose profile row may be blocked by RLS on initial load
        const { data: { session: s } } = await supabase.auth.getSession();
        const jwtRole =
          s?.user?.app_metadata?.role ||
          s?.user?.user_metadata?.role ||
          null;

        if (jwtRole === 'platform_admin') {
          logger.info('[AuthContext] Profile row not found — using JWT role fallback');
          setProfile({ id: userId, role: jwtRole });
        } else {
          // New user — profile row may not exist yet (trigger race)
          // Set a minimal profile so the app doesn't break
          logger.warn('[AuthContext] Profile row not found for user', { userId });
          setProfile({ id: userId, role: 'fan' });
        }
      } else {
        setProfile(data);
      }
    } catch (err) {
      setProfileError(err);
      // Don't wipe the profile on a transient error if we already have one
      setProfile(prev => prev ?? null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial session load
    supabase.auth.getSession()
      .then(({ data: { session: s }, error }) => {
        if (error) {
          logger.error('[AuthContext] getSession error', {
            code:    error.code,
            message: error.message,
          });
        }
        const resolved = s ?? null;
        setSession(resolved);
        setUser(resolved?.user ?? null);
        if (resolved?.user) {
          fetchProfile(resolved.user.id);
        } else {
          setProfile(null);
          setProfileLoading(false);
        }
      })
      .catch((err) => {
        logger.error('[AuthContext] getSession exception', { message: err?.message });
        setSession(null);
        setUser(null);
        setProfile(null);
        setProfileLoading(false);
      });

    // Auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      const resolved = s ?? null;
      setSession(resolved);
      setUser(resolved?.user ?? null);
      if (resolved?.user) {
        fetchProfile(resolved.user.id);
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const refetchProfile = useCallback(async () => {
    if (user?.id) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  const loading = session === undefined;

  return (
    <AuthContext.Provider value={{
      session,
      user,
      profile,
      loading,
      profileLoading,
      profileError,
      refetchProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);